import React, { useState } from "react";
import { Container, Box, Snackbar, Alert, CircularProgress } from "@mui/material";

import ConvertPP from "../components/ConvertPP";
import ConvertModal from "../components/ConvertModal";
import ConvertTable from "../components/ConvertTable";
import Seo from "../components/Seo";
import { trackEvent } from "../utils/analytics";
import API from "../api/api";

function ConvertPPPage() {
    const [analysisData, setAnalysisData] = useState(null);
    const [analyzeLoading, setAnalyzeLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [convertData, setConvertData] = useState([]);
    const [totals, setTotals] = useState(null);
    const [convertLoading, setConvertLoading] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("info");

    const showSnackbar = (msg, severity = "info") => {
        setSnackbarMessage(msg);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const resetBeforeAnalyze = () => {
        setAnalysisData(null);
        setConvertData([]);
        setTotals(null);
    };

    const handleConvert = async () => {
        if (!analysisData || !analysisData.files_ready || analysisData.files_ready.length === 0) {
            showSnackbar("Нет файлов для конвертации", "error");
            return;
        }

        if (convertLoading) {
            showSnackbar("Конвертация уже выполняется", "warning");
            return;
        }

        const formData = new FormData();
        const banksMap = {};

        analysisData.files_ready.forEach((f) => {
            if (!f.fileObj || f.bank === "unknown") return;

            if (!banksMap[f.bank]) banksMap[f.bank] = [];
            banksMap[f.bank].push(f.fileObj);
        });

        Object.entries(banksMap).forEach(([bank, files], index) => {
            formData.append(`banks[${index}][bank]`, bank);
            files.forEach((f) => formData.append(`banks[${index}][files]`, f));
        });

        setConvertLoading(true);
        setConvertData([]);
        setTotals(null);
        setModalOpen(false);

        trackEvent("convert_registry_started");

        try {
            const response = await API.post("/checker/convert-pp/", formData);
            const data = response.data;

            setConvertData(data.rows || []);
            setTotals(data.totals || null);

            trackEvent("convert_registry_finished", {
                rows_count: (data.rows || []).length,
            });

            showSnackbar("Конвертация завершена", "success");
        } catch (error) {
            console.error(error);

            const message =
                error.response?.data?.error ||
                "Ошибка соединения";

            showSnackbar(message, "error");
        } finally {
            setConvertLoading(false);
        }
    };

    return (
        <>
            <Seo
                title="Конвертация платежных поручений PDF в Excel – Qarzhy"
                description="Конвертация банковских платежных поручений из PDF в структурированный Excel-реестр сотрудников и выплат. Автоматическое извлечение ИИН, ФИО, счетов и сумм из платежных поручений."
                keywords="конвертация платежных поручений PDF в Excel, PDF платежные поручения в Excel, реестр выплат сотрудников, извлечение данных из PDF, Excel реестр выплат, обработка платежных поручений"
                url="https://qarzhy.kz/convert-pp"
            />

            <Container sx={{ mt: 4 }}>
                <ConvertPP
                    setAnalysisData={setAnalysisData}
                    setAnalyzeLoading={setAnalyzeLoading}
                    setModalOpen={setModalOpen}
                    setLoading={setLoading}
                    loading={loading}
                    convertLoading={convertLoading}
                    resetBeforeAnalyze={resetBeforeAnalyze}
                />

                <Box sx={{ mt: 5, display: "flex", justifyContent: "center" }}>
                    {convertLoading ? (
                        <CircularProgress size={60} />
                    ) : (
                        <ConvertTable
                            data={convertData}
                            totals={totals}
                        />
                    )}
                </Box>

                <ConvertModal
                    open={modalOpen}
                    onClose={() => {
                        if (analyzeLoading) return;
                        setModalOpen(false);
                    }}
                    analysisData={analysisData}
                    analyzeLoading={analyzeLoading}
                    onConvert={handleConvert}
                    convertLoading={convertLoading}
                />

                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={4000}
                    onClose={() => setSnackbarOpen(false)}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                    sx={{ mt: 7 }}
                >
                    <Alert
                        onClose={() => setSnackbarOpen(false)}
                        severity={snackbarSeverity}
                        sx={{ width: "100%" }}
                    >
                        {snackbarMessage}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
}

export default ConvertPPPage;